import { useEffect, useState } from 'react';
import { authApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './settings/Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@verlo/shared/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import CodeInput from '../../components/auth/CodeInput';
import TotpSetup from '../../components/auth/TotpSetup';

// Two-factor sign-in with an authenticator app (#179). Setting it up asks for the password
// again, then the first code the app shows; turning it off asks for the password and a code,
// so a signed-in laptop left open isn't enough. Verlo's staff can't turn it off — they have to
// have it — and an admin can reset someone's from Accounts if they lose their phone.

const TwoFactorCard = () => {
  const [status, setStatus] = useState(null); // { enabled, required, available }
  const [mode, setMode] = useState(null);     // 'setup' | 'disable'
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState(null);   // the API's secret and QR, once the password is in
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => authApi.mfaStatus()
    .then(({ data }) => setStatus(data))
    .catch(() => setStatus({ enabled: false, required: false, available: false }));

  useEffect(() => { load(); }, []);

  const open = (next) => {
    setMode(next);
    setPassword('');
    setCode('');
    setSetup(null);
    setError('');
  };
  const close = () => { setMode(null); setBusy(false); };

  const detail = (err) => err.response?.data?.detail || 'That didn’t go through. Please try again.';

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await authApi.mfaSetup(password);
      setSetup(data);
    } catch (err) {
      setError(detail(err));
    } finally {
      setBusy(false);
    }
  };

  const submitSetupCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await authApi.mfaVerify({ challenge_token: setup.challenge_token, code });
      setStatus(data);
      toast.success('Two-factor is on');
      close();
    } catch (err) {
      const message = detail(err);
      // The setup expired or took too many wrong codes: start again from the password.
      if (/start again/i.test(message)) setSetup(null);
      setCode('');
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await authApi.mfaDisable({ password, code });
      setStatus(data);
      toast.success('Two-factor is off');
      close();
    } catch (err) {
      setCode('');
      setError(detail(err));
    } finally {
      setBusy(false);
    }
  };

  const passwordField = (
    <div className="space-y-1.5">
      <Label htmlFor="mfa-password" className="text-xs text-ink-soft">Your password</Label>
      <Input
        id="mfa-password"
        type="password"
        autoComplete="current-password"
        autoFocus
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="mfa-password"
      />
    </div>
  );

  return (
    <Card data-testid="two-factor-card">
      <CardHeader>
        <CardTitle>Two-factor sign-in</CardTitle>
        <CardDescription>
          A code from an authenticator app on your phone, as well as your password, each time you
          sign in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status === null ? (
          <div className="flex items-center gap-2 text-sm text-ink-faint py-1">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : !status.available ? (
          <p className="text-sm text-ink-faint">Two-factor isn&apos;t available for this account.</p>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-ink" data-testid="mfa-state">
              {status.enabled ? 'On' : 'Off'}
              {status.required && (
                <span className="ml-2 text-[12px] text-ink-faint">Required for Verlo staff</span>
              )}
            </p>
            {!status.enabled && (
              <Button className="bg-verlo hover:bg-verlo-hover" size="sm" onClick={() => open('setup')}
                      data-testid="mfa-setup">
                Set up
              </Button>
            )}
            {status.enabled && !status.required && (
              <Button variant="outline" size="sm" onClick={() => open('disable')} data-testid="mfa-disable">
                Turn off
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={mode === 'setup'} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-sm" data-testid="mfa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Set up two-factor</DialogTitle>
            {!setup && <DialogDescription>First, confirm it&apos;s you.</DialogDescription>}
          </DialogHeader>
          <form onSubmit={setup ? submitSetupCode : submitPassword} className="space-y-4 py-2">
            {setup
              ? <TotpSetup setup={setup} code={code} onCodeChange={setCode} idPrefix="mfa-setup" />
              : passwordField}
            {error && <p className="text-[13px] text-signal-stop" data-testid="mfa-error">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button
                type="submit"
                className="bg-verlo hover:bg-verlo-hover"
                disabled={busy || (setup && code.length !== 6)}
                data-testid="mfa-setup-submit"
              >
                {busy ? 'Checking…' : setup ? 'Turn on' : 'Continue'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={mode === 'disable'} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-sm" data-testid="mfa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Turn off two-factor</DialogTitle>
            <DialogDescription>
              You&apos;ll sign in with your password alone. Confirm with your password and a code
              from the app.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDisable} className="space-y-4 py-2">
            {passwordField}
            <div className="space-y-1.5">
              <Label htmlFor="mfa-disable-code" className="text-xs text-ink-soft">Code from the app</Label>
              <CodeInput id="mfa-disable-code" value={code} onChange={setCode} autoFocus={false}
                         data-testid="mfa-disable-code" />
            </div>
            {error && <p className="text-[13px] text-signal-stop" data-testid="mfa-error">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button type="submit" variant="outline" className="text-signal-stop"
                      disabled={busy || code.length !== 6} data-testid="mfa-disable-submit">
                {busy ? 'Checking…' : 'Turn off'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TwoFactorCard;
